import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  doc,
  onSnapshot,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Get count of unread messages for a user
 * @param {string} userId - The user's ID
 * @param {boolean} isPartner - Whether the user is a partner
 * @returns {Promise<number>} - Promise resolving to the number of unread messages
 */
export const getUnreadMessagesCount = async (userId, isPartner) => {
  try {
    console.log('Getting unread count for', userId, 'isPartner:', isPartner);
    const messagesRef = collection(db, 'messages');
    let q;
    
    if (isPartner) {
      // If user is partner, count messages where they are the receiver and messages are unread
      q = query(
        messagesRef,
        where('receiverId', '==', userId),
        where('isRead', '==', false)
      );
    } else {
      // For non-partners
      q = query(
        messagesRef,
        where('senderId', '==', userId),
        where('isRead', '==', false)
      );
    }
    
    const querySnapshot = await getDocs(q);
    console.log('Unread count:', querySnapshot.size);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread messages count:', error);
    throw error;
  }
};

/**
 * Set up real-time listener for unread messages
 * @param {string} userId - The user's ID
 * @param {boolean} isPartner - Whether the user is a partner
 * @param {Function} callback - Callback function to be called with unread count
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUnreadMessages = (userId, isPartner, callback) => {
  try {
    const messagesRef = collection(db, 'messages');
    let q;
    
    if (isPartner) {
      // If user is partner, listen to messages where they are the receiver and messages are unread
      q = query(
        messagesRef,
        where('receiverId', '==', userId),
        where('isRead', '==', false)
      );
    } else {
      // For non-partners
      q = query(
        messagesRef,
        where('senderId', '==', userId),
        where('isRead', '==', false)
      );
    }
    
    return onSnapshot(q, (snapshot) => {
      console.log('Subscription update, unread count:', snapshot.size);
      callback(snapshot.size);
    }, (error) => {
      console.error('Error in message subscription:', error);
    });
  } catch (error) {
    console.error('Error subscribing to unread messages:', error);
    throw error;
  }
};

/**
 * Mark all messages in a conversation as read
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The current user's ID
 * @returns {Promise<boolean>} - Promise resolving to true if successful
 */
export const markConversationAsRead = async (conversationId, userId) => {
  try {
    console.log('Marking conversation as read:', conversationId, 'for user:', userId);
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      where('receiverId', '==', userId),
      where('isRead', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    console.log('Found', querySnapshot.size, 'unread messages to mark as read');
    
    const updatePromises = querySnapshot.docs.map(docSnap => {
      console.log('Marking message as read:', docSnap.id);
      return updateDoc(doc(db, 'messages', docSnap.id), {
        isRead: true,
        readAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    return true;
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

/**
 * Get all unread messages for a user
 * @param {string} userId - The user's ID
 * @param {boolean} isPartner - Whether the user is a partner
 * @returns {Promise<Array>} - Promise resolving to an array of unread messages
 */
export const getUnreadMessages = async (userId, isPartner) => {
  try {
    const messagesRef = collection(db, 'messages');
    let q;
    
    if (isPartner) {
      // If user is partner, get messages where they are the receiver and messages are unread
      q = query(
        messagesRef,
        where('receiverId', '==', userId),
        where('isRead', '==', false),
        orderBy('timestamp', 'desc')
      );
    } else {
      // For non-partners
      q = query(
        messagesRef,
        where('senderId', '==', userId),
        where('isRead', '==', false),
        orderBy('timestamp', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting unread messages:', error);
    throw error;
  }
}; 